export interface ServerClip {
  id: string;
  status: "pending" | "processing" | "failed";
  errorCode: string | null;
  createdAt: string;
  durationSeconds: number | null;
  retryable: boolean;
}

export interface LocalUpload {
  localId: string;
  /** upload_failed can be retried; upload_rejected (HTTP 4xx on create) cannot. */
  status: "uploading" | "upload_failed" | "upload_rejected";
  progress: number;
  createdAt: string;
}

export type VoiceClipView =
  | { kind: "local"; key: string; upload: LocalUpload }
  | { kind: "server"; key: string; clip: ServerClip };

const FAILURE_MESSAGES: Record<string, string> = {
  upload_invalid: "The upload was incomplete",
  dispatch_failed: "Couldn't start processing",
  transcription_failed: "Couldn't transcribe the recording",
  no_speech: "No speech detected",
  handler_failed: "Couldn't save the note",
  worker_lost: "Processing stopped",
  audio_missing: "The recording has expired",
};

const LOCAL_MESSAGES: Record<Exclude<LocalUpload["status"], "uploading">, string> = {
  upload_failed: "Upload failed",
  upload_rejected: "This recording can't be sent. It may be too long or in an unsupported format.",
};

/** Server clips oldest first, then local uploads (always the newest). */
export function mergeClips(local: LocalUpload[], server: ServerClip[]): VoiceClipView[] {
  const serverViews: VoiceClipView[] = [...server]
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((clip) => ({ kind: "server", key: `server:${clip.id}`, clip }));
  const localViews: VoiceClipView[] = local.map((upload) => ({
    kind: "local",
    key: `local:${upload.localId}`,
    upload,
  }));
  return [...serverViews, ...localViews];
}

export function hasActiveClips(server: ServerClip[]): boolean {
  return server.some((clip) => clip.status === "pending" || clip.status === "processing");
}

/**
 * Clips that were in flight, or were just submitted, and have left the open
 * list: their result exists now. Submitted ids cover a worker that finishes
 * before any poll saw the clip pending.
 */
export function finishedClipIds(previous: ServerClip[], current: ServerClip[], submitted: string[]): string[] {
  const stillOpen = new Set(current.map((clip) => clip.id));
  const inFlight = new Set([
    ...previous.filter((clip) => clip.status === "pending" || clip.status === "processing").map((clip) => clip.id),
    ...submitted,
  ]);
  return Array.from(inFlight).filter((id) => !stillOpen.has(id));
}

/** One fetch of the open list. seq grows with every fetch started. */
export interface ClipSnapshot {
  clips: ServerClip[];
  seq: number;
}

/** A clip whose submit resolved after the fetch numbered afterSeq started. */
export interface SubmittedClip {
  id: string;
  afterSeq: number;
}

/**
 * Apply one fetch of the open list. A submitted clip is judged only against a
 * list fetched after its submit, then drops out: it either finished, or it is
 * open and the previous list tracks it from here. That keeps each clip
 * reported once instead of on every poll.
 */
export function applySnapshot<T extends SubmittedClip>(
  previous: ServerClip[],
  snapshot: ClipSnapshot,
  submitted: T[],
): { finished: string[]; submitted: T[] } {
  const judged = submitted.filter((entry) => snapshot.seq > entry.afterSeq);
  return {
    finished: finishedClipIds(previous, snapshot.clips, judged.map((entry) => entry.id)),
    submitted: submitted.filter((entry) => snapshot.seq <= entry.afterSeq),
  };
}

export function clipMessage(view: VoiceClipView): string {
  if (view.kind === "local") {
    return view.upload.status === "uploading"
      ? `Uploading ${Math.round(view.upload.progress * 100)}%`
      : LOCAL_MESSAGES[view.upload.status];
  }
  if (view.clip.status !== "failed") return "Transcribing…";
  return FAILURE_MESSAGES[view.clip.errorCode ?? ""] ?? "Something went wrong";
}
